import React from 'react';
import { useTranslation } from 'react-i18next';
import { FilterTabItem } from 'components/common/FilterTab';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import FilterTab from 'components/common/FilterTab';
import SectionHeader from './SectionHeader';
import ColumnVisibilityDropdown from './ColumnVisibilityDropdown';
import TableContainer from './TableContainer';
import EmptyState from './EmptyState';
import { STYLES } from './types';

interface OrdersViewProps {
  transactionId: string;
  orders: any[];
  columns: any[];
  columnOptions: Array<{id: string; label: string; visible: boolean}>;
  onToggleColumn: (id: string, visible: boolean) => void;
  tabItems: FilterTabItem[];
  onTabClick: (value: string) => void;
}

/**
 * Component for displaying orders in a transaction
 */
const OrdersView: React.FC<OrdersViewProps> = ({
  transactionId,
  orders,
  columns,
  columnOptions,
  onToggleColumn,
  tabItems,
  onTabClick
}) => {
  const { t } = useTranslation();
  // Set up the table
  const ordersTable = useAdvanceTable({
    data: orders,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <>
      <SectionHeader title={t('transactions.detail.totalOrders', 'Total Orders: {{count}}', { count: orders.length })}>
        <ColumnVisibilityDropdown 
          options={columnOptions} 
          onToggle={onToggleColumn}
        />
      </SectionHeader>
      
      <FilterTab tabItems={tabItems} className="mb-2" />
      
      <AdvanceTableProvider {...ordersTable}>
        <TableContainer loading={false}>
          {orders.length === 0 ? (
            <EmptyState message={t('transactions.detail.noOrdersText', 'No orders found for this transaction')} />
          ) : (
            <>
              <AdvanceTable tableProps={STYLES.table.props} />
              <AdvanceTableFooter 
                pagination 
                totalCount={orders.length} 
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </TableContainer>
      </AdvanceTableProvider>
    </>
  );
};

export default React.memo(OrdersView);
